// The People tab of the want page (#222 slice F; spec
// docs/superpowers/specs/2026-11-02-wantlist-redesign-design.md, "People is a
// client pivot, scoped on the server"). The want page's availability read
// already lists, per entry, the Claims holding it (`availability_for_viewer`,
// one holder per Claim, each naming its Claimant); People pivots those
// holders into one row per Claimant. Pure: no stores, no fetches.

import { PEOPLE_CARDS_DESKTOP, PEOPLE_CARDS_PHONE } from './config.js';

/** @typedef {import('../types.js').WantEntry} WantEntry */

/**
 * One tile of a person's: a printing of a want's card they hold, identified
 * by the card name and the printing (one tile per held printing, however many
 * of the page's wants it answers and however many Claims hold it).
 * @typedef {Object} PersonCard
 * @property {string} key - the held printing's identity (card name + 'set/collector/finish')
 * @property {string} name
 * @property {string | null} imageId - the held printing's image (Scryfall id), else the
 *   first want's representative printing
 * @property {boolean} foil - the printing held is foil
 * @property {number} quantity - its copies across their Claims
 * @property {number | null} price - the printing's price (the want's "from" floor when it has none)
 * @property {string | null} binderUrl - a binder of theirs holding it
 */

/**
 * @typedef {Object} Person
 * @property {string} uid - the Claimant's uid
 * @property {string} name
 * @property {string | null} handle - their user page handle, when they have one
 * @property {string[]} sharedGroupIds - the server Groups connecting the viewer
 *   to them, as the server decides (never a server they are dark in)
 * @property {number} wants - how many of the page's wants they hold (the ranking)
 * @property {PersonCard[]} cards - their held copies, most valuable first
 */

/** @param {unknown} v */
const norm = (v) => (v == null ? '' : String(v).trim().toLowerCase());

/**
 * A want's identity on the People tab: the card name and its Card
 * Specificity (set, printing, finish). The same want in two bundles (the
 * Default Want List and a Project) is one want; "any Sol Ring" and "a foil
 * Sol Ring" are two, so the ranking counts the page's wants.
 * @param {WantEntry} entry
 * @returns {string}
 */
export function wantKey(entry) {
  return [entry.card_name, entry.set_code, entry.collector_number, entry.finish]
    .map(norm)
    .join('|');
}

/**
 * Pivot the page's entries into the people holding them: one Person per
 * Claimant. Their ranking counts the page's wants they hold (`wants`); their
 * tiles are the printings they hold (`cards`, one per printing in each
 * holder's `held_copies`): one foil Sol Ring answering both "any Sol Ring" and "foil
 * Sol Ring" is two wants and one tile. A tile's copies are summed across
 * their Claims (a Claim's copies of a printing counted once however many
 * wants find them), linked to the first Claim's binder.
 *
 * Who is listed: never the list's owner (their own Claims holding their own
 * wants are not a trade), so the viewer appears only on someone else's list.
 * `mineOnly` (a "Mine Only" browse selection, which persists from the browse
 * pages) keeps just the viewer's holdings, so on your own list it is empty.
 * The server already narrowed the holders to the picker's Reach.
 * @param {WantEntry[]} entries - one owner's want entries, each with `holders`
 * @param {{ viewerUid?: string | null, mineOnly?: boolean }} [opts]
 * @returns {Person[]}
 */
export function pivotPeople(entries, { viewerUid = null, mineOnly = false } = {}) {
  // A want listed twice matched the same binder items twice; its first entry
  // answers it.
  /** @type {Map<string, WantEntry>} */
  const wants = new Map();
  for (const entry of entries) {
    const key = wantKey(entry);
    if (!wants.has(key)) wants.set(key, entry);
  }

  /** @type {Map<string, Person>} */
  const byUid = new Map();
  /** @type {Map<string, Set<string>>} uid -> the want keys they hold */
  const wantsOf = new Map();
  /** @type {Map<string, PersonCard>} `${uid}|${tile key}` -> their tile */
  const tileOf = new Map();
  /** @type {Set<string>} `${uid}|${tile key}|${binder}`: copies already counted */
  const counted = new Set();
  for (const [key, entry] of wants) {
    for (const h of entry.holders ?? []) {
      const uid = h.claimant_uid;
      if (!uid || uid === entry.user_id || (mineOnly && uid !== viewerUid)) continue;
      let person = byUid.get(uid);
      if (!person) {
        const name = h.owner_name ?? h.owner_handle ?? uid;
        person = {
          uid,
          name,
          handle: h.owner_handle ?? null,
          sharedGroupIds: h.shared_group_ids ?? [],
          wants: 0,
          cards: [],
        };
        byUid.set(uid, person);
        wantsOf.set(uid, new Set());
      }
      /** @type {Set<string>} */ (wantsOf.get(uid)).add(key);

      for (const copies of h.held_copies ?? []) {
        const printing = `${copies.set_code}/${copies.collector_number}/${copies.finish}`;
        const tileKey = `${norm(entry.card_name)}|${printing}`;
        const id = `${uid}|${tileKey}`;
        const price = copies.price ?? entry.sample_price ?? null;
        let tile = tileOf.get(id);
        if (!tile) {
          tile = {
            key: tileKey,
            name: entry.card_name,
            imageId: copies.scryfall_id ?? entry.sample_scryfall_id ?? null,
            foil: copies.finish === 'foil',
            quantity: 0,
            price,
            binderUrl: h.binder_url ?? null,
          };
          tileOf.set(id, tile);
          person.cards.push(tile);
        } else if (price != null && (tile.price == null || price < tile.price)) {
          tile.price = price;
        }
        // One Claim's copies of a printing count once, whichever want found them.
        const claimCopies = `${id}|${h.binder_url ?? h.binder_name ?? ''}`;
        if (!counted.has(claimCopies)) {
          counted.add(claimCopies);
          tile.quantity += copies.quantity ?? 1;
        }
      }
    }
  }

  const people = [...byUid.values()];
  for (const person of people) {
    person.wants = /** @type {Set<string>} */ (wantsOf.get(person.uid)).size;
    person.cards.sort(byValueThenName);
  }
  return people.sort(byRank);
}

/** @param {PersonCard} card */
const valueOf = (card) => card.price ?? 0;

/** Most valuable first: the representative few lead with what matters. */
function byValueThenName(/** @type {PersonCard} */ a, /** @type {PersonCard} */ b) {
  return valueOf(b) - valueOf(a) || a.name.localeCompare(b.name);
}

/** @param {Person} person */
const totalValue = (person) => person.cards.reduce((sum, c) => sum + valueOf(c), 0);

/**
 * Who holds the most of the page's wants first; ties go to the larger value
 * held, then to the name (then the uid, so the order never depends on input).
 */
function byRank(/** @type {Person} */ a, /** @type {Person} */ b) {
  return (
    b.wants - a.wants ||
    totalValue(b) - totalValue(a) ||
    a.name.localeCompare(b.name) ||
    a.uid.localeCompare(b.uid)
  );
}

/**
 * A row's representative few (VISUALS.md "A list of X shows a representative
 * few of what each X holds"): the cards a desktop shows (a phone shows the
 * first PEOPLE_CARDS_PHONE of them) and the "+N" each counts for the rest.
 * @template T
 * @param {T[]} items - already in the order to show them
 * @returns {{ shown: T[], more: { desktop: number, phone: number } }}
 */
export function representativeFew(items) {
  return {
    shown: items.slice(0, PEOPLE_CARDS_DESKTOP),
    more: {
      desktop: Math.max(0, items.length - PEOPLE_CARDS_DESKTOP),
      phone: Math.max(0, items.length - PEOPLE_CARDS_PHONE),
    },
  };
}

/**
 * A person's row chips: the groups the server says connect the viewer to
 * them (`sharedGroupIds`, visibility_sql's one rule, so a server they are
 * dark in never shows), named from the viewer's own groups, in the viewer's
 * order. A Discord server is one group, as everywhere on the web.
 * @template {{ groupId: string }} G
 * @param {string[]} sharedGroupIds
 * @param {G[]} userGroups
 * @returns {G[]}
 */
export function sharedGroupsOf(sharedGroupIds, userGroups) {
  const ids = new Set(sharedGroupIds);
  return userGroups.filter((g) => ids.has(g.groupId));
}
